import { bindFieldValue, type BoundField } from '@lib/helpers/field-value'
import type { FieldDto, FieldValueDto } from '@lib/dto/envelope.dto'

function draftFilled(bound: BoundField): boolean {
  switch (bound.type) {
    case 'checkbox':
      return bound.value === true
    case 'signature':
    case 'initials':
      return bound.value !== undefined && bound.value.data.trim().length > 0
    case 'radio':
    case 'dropdown':
    case 'text':
    case 'date':
    case 'date_signed':
      return bound.value !== undefined && bound.value.trim().length > 0
  }
}

export function isFieldFilled(
  field: FieldDto,
  value: FieldValueDto | undefined,
): boolean {
  return draftFilled(bindFieldValue(field, value))
}

export function fieldNeedsInk(field: FieldDto): boolean {
  if (field.type === 'date_signed') {
    return false
  }
  return !field.readOnly
}

/**
 * The required fields still to fill, which is what Finish waits for and what
 * the page points the signer to: required, writable fields other than the
 * signing date, plus each required radio group with nothing chosen (as its
 * first button). Matches the server's check on complete.
 */
export function remainingRequired(
  fields: readonly FieldDto[],
  values: Record<string, FieldValueDto>,
): FieldDto[] {
  const remaining: FieldDto[] = []
  const groups = new Map<string, FieldDto[]>()
  for (const field of fields) {
    if (field.type === 'radio') {
      if (field.group != null) {
        groups.set(field.group, [...(groups.get(field.group) ?? []), field])
      }
      continue
    }
    if (!field.required || field.readOnly || field.type === 'date_signed') {
      continue
    }
    if (!isFieldFilled(field, values[field.id])) {
      remaining.push(field)
    }
  }
  for (const groupFields of groups.values()) {
    const [first] = groupFields
    if (first === undefined || !groupFields.some((field) => field.required)) {
      continue
    }
    const selected = groupFields.some((field) => {
      const bound = bindFieldValue(field, values[field.id])
      return bound.type === 'radio' && bound.value !== undefined && bound.value.length > 0
    })
    if (!selected) {
      remaining.push(first)
    }
  }
  return remaining
}
